import { type ReactNode } from "react";
import { cn } from "@/lib/cn";

/** Small labeled stat used in detail grids. */
export function Stat({
  label,
  value,
  mono = false,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="mb-0.5 text-xs text-fg-muted">{label}</div>
      <div className={cn("text-sm text-fg-primary", mono && "font-mono tabular-nums")}>
        {value}
      </div>
    </div>
  );
}

/** KPI tile — mono value over a muted label (DESIGN-SYSTEM §9.5). */
export function KpiTile({
  label,
  value,
  tone,
}: {
  label: ReactNode;
  value: ReactNode;
  tone?: "default" | "success" | "warning" | "danger";
}) {
  const toneClass =
    tone === "success"
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : tone === "danger"
          ? "text-danger"
          : "text-fg-primary";
  return (
    <div className="rounded-xl border border-border bg-bg-primary/40 p-3">
      <div className={cn("font-mono text-xl font-semibold tabular-nums leading-tight", toneClass)}>
        {value}
      </div>
      <div className="mt-1 text-xs text-fg-muted">{label}</div>
    </div>
  );
}

/** A simple two-column "definition" row used in the Overview state list. */
export function StateRow({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-fg-muted">{label}</span>
      <span className="flex items-center gap-2 text-right text-sm text-fg-primary">
        {children}
      </span>
    </div>
  );
}
